/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import MilProductComponent from '../../../components/MilProductComponent';
import { IImageSettings } from '@msdyn365-commerce/core';
import { ProductSearchResult, ProductWarehouseInventoryInformation } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IGetMultiUnitOfMeasureConversionResponse } from '../../../actions/DataServiceEntities.g';
import { IMilSearchResultContainerResources } from '../mil-search-result-container.props.autogenerated';
import { ICustomerWarehouseEntity_V3 } from '../../../actions/DataServiceEntities.g';
import { displayedInventoryMethod } from '../../mil-quick-order/mil-quick-order.props.autogenerated';

interface IProductSearchResultItems {
    products: ProductSearchResult[];
    context: any;
    resources: IMilSearchResultContainerResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickviewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    desiredNonstockCommerceName?: string;
    nonstockCommerceState?: string;
    uomConvData?: IGetMultiUnitOfMeasureConversionResponse;
    estimatedAvailabilities?: ProductWarehouseInventoryInformation;
    warehouses?: ICustomerWarehouseEntity_V3[];
}

interface ICustInv {
    inventory: number;
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
export function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    products,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    productComparisonButton,
    channelInventoryConfigurationId,
    isPriceMinMaxEnabled,
    productsDimensionAvailabilities,
    desiredNonstockCommerceName,
    nonstockCommerceState,
    uomConvData: propUomConvData,
    estimatedAvailabilities: propEstimated,
    warehouses: propWarehouses
}) => {
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const inventoryMethod = context.app.config.displayedInventoryMethod;

    return (
        <ul className='list-unstyled'>
            {!!propUomConvData?.UomConvMap?.length &&
                products.map((product: ProductSearchResult, index: number) => {
                    const productBehaviour = propUomConvData?.UomConvMap?.find(prod => prod.ItemRecId === product.RecordId);
                    const availInvs = propEstimated?.ProductWarehouseInventoryAvailabilities?.filter(
                        inv => inv.ProductId === product.RecordId
                    );
                    const newCustInv: ICustInv[] = [];
                    if (propWarehouses && availInvs) {
                        propWarehouses.map(wh => {
                            const inventoryInfo = availInvs.find(inv => inv.InventLocationId === wh.AvailableWarehouse);
                            if (inventoryInfo) {
                                const newWhEntry = {
                                    inventory:
                                        inventoryMethod === displayedInventoryMethod.physicalAvailable
                                            ? inventoryInfo.PhysicalAvailable || 0
                                            : inventoryInfo.TotalAvailable || 0
                                };
                                newCustInv.push(newWhEntry);
                            }
                        });
                    }

                    const aggProductInvAvail = propEstimated?.AggregatedProductInventoryAvailabilities?.find(
                        inv => inv.ProductId === product.RecordId
                    );
                    const totalInv =
                        propWarehouses && propWarehouses.length > 0 && newCustInv
                            ? newCustInv?.reduce((acc, curr) => acc + (curr.inventory >= 0 ? curr.inventory : 0), 0)
                            : aggProductInvAvail && inventoryMethod === displayedInventoryMethod.physicalAvailable
                            ? aggProductInvAvail?.PhysicalAvailableQuantity
                            : aggProductInvAvail?.TotalAvailableQuantity;
                    return (
                        <li className='ms-product-search-result__item' key={index}>
                            <MilProductComponent
                                context={context}
                                telemetryContent={telemetryContent}
                                imageSettings={imageSettings}
                                freePriceText={resources.priceFree}
                                originalPriceText={resources.originalPriceText}
                                currentPriceText={resources.currentPriceText}
                                shouldDisplayPrice={!!propUomConvData}
                                ratingAriaLabel={resources.ratingAriaLabel}
                                allowBack={allowBackNavigation}
                                id={moduleId}
                                key={product.RecordId}
                                typeName={moduleType}
                                data={{ product }}
                                quickViewButton={quickviewSlot}
                                productComparisonButton={productComparisonButton}
                                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                                isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                                priceResources={priceResources}
                                swatchItemAriaLabel={resources.swatchItemAriaLabel}
                                dimensionAvailabilities={productsDimensionAvailabilities?.find(
                                    dimensionAvailability =>
                                        ArrayExtensions.hasElements(dimensionAvailability) &&
                                        dimensionAvailability[0].masterProductId === product.MasterProductId
                                )}
                                desiredNonstockCommerceName={desiredNonstockCommerceName || ''}
                                nonstockCommerceState={nonstockCommerceState || ''}
                                stockStatusText={resources.stockStatusText}
                                productBehavior={productBehaviour?.Product?.Behavior}
                                minimumOrderQtyLabelText={resources.minimumOrderQtyLabelText}
                                availableStockLabel={resources.availableStockLabel}
                                availInvs={totalInv || 0}
                                productUom={productBehaviour}
                                isEnabledProductDescription={true}
                            />
                        </li>
                    );
                })}
        </ul>
    );
};
